<template>
  <div :class="$style.base">
    <MainItems
      :items="filterLinks(mainItems)"
      @navItemClick="close"
    />
    <div :class="$style.scroll">
      <Flex
        direction="column"
        fullWidth
      >
        <SecondaryItems
          :items="filterLinks(navItems)"
          @navItemClick="close"
        />
        <Flex
          direction="column"
          :gap="0.5"
          fullWidth
        >
          <SecondaryItems
            :items="filterLinks(secondaryNavItems)"
            @navItemClick="close"
          />
          <Flex
            wrap
            align="center"
            justify="between"
          >
            <MobileDesktopToggle v-if="showMobileDesktopButton" />
            <ThemeToggle />
          </Flex>
          <div
            v-if="hasFeatureFlag('trade-show-demo-account')"
            :class="$style.tradeShowButton"
            @click="showDemoOnboarding = true"
            v-test="'onboardingIconMobile'"
          >
            <BaseIcon
              name="play-circle"
              color="inverted"
              size="s"
            />
            <BaseText color="inverted">
              {{ t('onboarding.start_demo') }}
            </BaseText>
          </div>
        </Flex>
      </Flex>
    </div>
    <Footer :class="$style.footer" />
    <DemoOnboarding
      v-if="showDemoOnboarding"
      @close="showDemoOnboarding = false"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import DemoOnboarding from '@/app/logged-in/header/DemoOnboarding.vue';
import ThemeToggle from '@/app/logged-in/header/ThemeToggle.vue';
import type { IconName } from '@/components/base-icon/types';
import { unreadNewsCount } from '@/product-fruits';
import { isRouteAllowed } from '@/router/helpers';
import Footer from './Footer.vue';
import MainItems from './MainItems.vue';
import MobileDesktopToggle from './MobileDesktopToggle.vue';
import SecondaryItems from './SecondaryItems.vue';

const router = useRouter();
const { t } = useI18n();

import { useUserStore } from '@/stores/user';
import { browser, device } from '@/user-context';
import useOnline from '../online';

const { hideOnlineRoutes, showOnlineRoutesNotification } = useOnline();
const { user } = useUserStore();

const emit = defineEmits(['navItemClick']);

export interface NavItem {
  icon: IconName;
  routeName?: string;
  name: string;
  notification?: {
    show?: boolean;
    value?: number | string;
  };
  hide?: boolean;
  isNewsWidget?: boolean;
}

const filterLinks = (links: NavItem[]) =>
  links.filter((link) => {
    if (!link.routeName) {
      return !link.hide;
    }
    const route = router.resolve({ name: link.routeName });
    return !!route && !link.hide && isRouteAllowed(route);
  });

const { hasFeatureFlag } = useUserStore();
const showDemoOnboarding = ref(false);
const showMobileDesktopButton = computed(
  () =>
    hasFeatureFlag('mobile-desktop-button') &&
    device.mobile &&
    (!browser.isUIWebview || browser.isAndroid),
);

const mainItems = computed<NavItem[]>(() => [
  {
    icon: 'home',
    routeName: 'dashboard',
    name: t('nav.dashboard'),
  },

  {
    icon: 'calendar',
    routeName: 'calendar',
    name: t('nav.calendar'),
  },
  {
    icon: 'shopping-cart',
    routeName: 'register',
    name: t('nav.register'),
  },
]);

const navItems = computed<NavItem[]>(() => [
  {
    icon: 'people',
    routeName: 'customers',
    name: t('nav.customers'),
  },
  {
    icon: 'pricetag',
    routeName: 'products',
    name: t('nav.products'),
  },
  {
    icon: 'email',
    routeName: 'messages',
    name: t('nav.messages'),
  },
  {
    icon: 'message',
    routeName: 'feedback',
    name: t('nav.feedback'),
    notification: {
      show: (user && user.feedbacks > 0) || undefined,
      value: user?.feedbacks,
    },
  },
  {
    icon: 'activity',
    routeName: 'reports',
    name: t('nav.reports'),
  },
  {
    icon: 'marketing',
    routeName: 'marketing',
    name: t('nav.marketing'),
  },
  {
    icon: 'flash',
    routeName: 'online',
    name: t('nav.online'),
    hide: hideOnlineRoutes.value,
    notification: {
      show: showOnlineRoutesNotification.value,
      value: '',
    },
  },
]);

const secondaryNavItems = computed<NavItem[]>(() => [
  {
    icon: 'person',
    routeName: 'account',
    name: t('nav.account'),
  },
  {
    icon: 'bulb',
    name: t('nav.whats_new'),
    isNewsWidget: true,
    notification: {
      show: unreadNewsCount.value > 0,
      value: unreadNewsCount.value,
    },
  },
  {
    icon: 'settings',
    routeName: 'admin',
    name: t('nav.settings'),
  },
  {
    icon: 'info',
    routeName: 'support-hub',
    name: t('nav.support_hub'),
  },
]);

const close = () => {
  emit('navItemClick');
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: $spacing;
  background-color: var(--surface-nav);
  padding: $spacing * 0.75;

  @media (max-height: 710px) {
    padding: $spacing * 0.5 $spacing * 0.75;
  }
}

.scroll {
  overflow-y: auto;
  flex-grow: 1;
}

.inner {
  display: flex;
  flex-direction: column;
  gap: $spacing;
  width: 100%;
}

.tradeShowButton {
  display: flex;
  align-items: center;
  gap: $spacing;
  padding: $spacing * 0.75 $spacing;
  background: var(--surface-nav);
  border: 1px solid var(--color-primary-dark);
  border-radius: $radius;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);
  cursor: pointer;

  // Same as the desktop nav in dark mode
  @include theme('dark') {
    border-color: var(--border);
  }
}

.footer {
  margin-bottom: $spacing * 1.5;
}
</style>
